import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Loader2, RefreshCw, Star } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth';
import type { Language } from '@/types';
import { fetchQCReports, type QCReportRow } from '../api';

interface QCWorkspaceProps {
  language: Language;
  onSelectSite: (siteId: string) => void;
}

type SiteListing = {
  id: string;
  name: string;
  address: string;
  customer_number: string | null;
  active: boolean;
};

type SortKey = 'recent_qc' | 'oldest_qc' | 'no_qc' | 'name_asc';

type SiteLastCleaning = {
  siteId: string;
  scheduledFor: string;
};

const copy = {
  en: {
    title: 'Quality Control',
    description: 'Review cleaning quality across BRFs.',
    search: 'Search BRF, address or customer number',
    sort: 'Sort',
    sortRecent: 'Recent QC first',
    sortOldest: 'Oldest QC first',
    sortNoQc: 'Never QCd first',
    sortName: 'Name (A–Z)',
    refresh: 'Refresh',
    colBrf: 'BRF',
    colLastQc: 'Last QC',
    colScore: 'Score',
    colLastCleaning: 'Last cleaning',
    colAction: '',
    open: 'Open',
    loading: 'Loading sites…',
    empty: 'No BRFs match.',
    none: '—',
  },
  sv: {
    title: 'Kvalitetskontroll',
    description: 'Granska städkvalitet per BRF.',
    search: 'Sök BRF, adress eller kundnummer',
    sort: 'Sortera',
    sortRecent: 'Senast QC först',
    sortOldest: 'Äldsta QC först',
    sortNoQc: 'Aldrig QC först',
    sortName: 'Namn (A–Ö)',
    refresh: 'Uppdatera',
    colBrf: 'BRF',
    colLastQc: 'Senaste QC',
    colScore: 'Poäng',
    colLastCleaning: 'Senaste städning',
    colAction: '',
    open: 'Öppna',
    loading: 'Laddar BRF:er…',
    empty: 'Inga BRF:er matchar.',
    none: '—',
  },
} as const;

function StarRow({ score }: { score: 1 | 2 | 3 | 4 | 5 | null }) {
  if (score == null) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={
            n <= score
              ? 'h-4 w-4 fill-primary text-primary'
              : 'h-4 w-4 text-muted-foreground/40'
          }
        />
      ))}
    </div>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return iso.slice(0, 10);
}

export function QCWorkspace({ language, onSelectSite }: QCWorkspaceProps) {
  const text = copy[language];
  const { session } = useAuth();
  const token = session?.access_token ?? null;

  const [sites, setSites] = useState<SiteListing[]>([]);
  const [reports, setReports] = useState<QCReportRow[]>([]);
  const [lastCleanings, setLastCleanings] = useState<SiteLastCleaning[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('recent_qc');

  const loadAll = async () => {
    if (!token) return;
    setIsLoading(true);
    setError(null);
    try {
      const [sitesRes, jobsRes, qcReports] = await Promise.all([
        supabase
          .from('sites')
          .select('id, name, address, customer_number, active')
          .eq('active', true)
          .order('name'),
        supabase
          .from('jobs')
          .select('site_id, scheduled_for, status')
          .in('status', ['completed', 'approved'])
          .order('scheduled_for', { ascending: false })
          .limit(2000),
        fetchQCReports(token),
      ]);

      if (sitesRes.error) throw sitesRes.error;
      if (jobsRes.error) throw jobsRes.error;

      const nextSites: SiteListing[] = (sitesRes.data ?? []).map((s) => ({
        id: s.id,
        name: s.name,
        address: s.address ?? '',
        customer_number: s.customer_number ?? null,
        active: Boolean(s.active),
      }));

      // most recent cleaning per site
      const seen = new Set<string>();
      const latest: SiteLastCleaning[] = [];
      for (const row of jobsRes.data ?? []) {
        if (seen.has(row.site_id)) continue;
        seen.add(row.site_id);
        latest.push({ siteId: row.site_id, scheduledFor: row.scheduled_for });
      }

      setSites(nextSites);
      setLastCleanings(latest);
      setReports(qcReports);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const lastQcBySite = useMemo(() => {
    const map = new Map<string, QCReportRow>();
    for (const r of reports) {
      const current = map.get(r.site_id);
      if (!current || r.inspected_at > current.inspected_at) {
        map.set(r.site_id, r);
      }
    }
    return map;
  }, [reports]);

  const lastCleaningBySite = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of lastCleanings) map.set(row.siteId, row.scheduledFor);
    return map;
  }, [lastCleanings]);

  const filteredSites = useMemo(() => {
    const query = search.trim().toLowerCase();
    const base = query.length === 0
      ? sites
      : sites.filter((site) => {
          const haystack = `${site.name} ${site.address} ${site.customer_number ?? ''}`.toLowerCase();
          return haystack.includes(query);
        });

    const sorted = [...base];
    sorted.sort((a, b) => {
      if (sortKey === 'name_asc') {
        return a.name.localeCompare(b.name, 'sv');
      }
      const aQc = lastQcBySite.get(a.id)?.inspected_at ?? null;
      const bQc = lastQcBySite.get(b.id)?.inspected_at ?? null;
      if (sortKey === 'recent_qc') {
        if (aQc && bQc) return bQc.localeCompare(aQc);
        if (aQc) return -1;
        if (bQc) return 1;
        return a.name.localeCompare(b.name, 'sv');
      }
      if (sortKey === 'oldest_qc') {
        if (aQc && bQc) return aQc.localeCompare(bQc);
        if (aQc) return -1;
        if (bQc) return 1;
        return a.name.localeCompare(b.name, 'sv');
      }
      // no_qc: never-inspected first, then alphabetical
      if (!aQc && bQc) return -1;
      if (aQc && !bQc) return 1;
      return a.name.localeCompare(b.name, 'sv');
    });
    return sorted;
  }, [sites, search, sortKey, lastQcBySite]);

  return (
    <div className="space-y-6 pb-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{text.title}</h1>
          <p className="text-sm text-muted-foreground">{text.description}</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => void loadAll()} disabled={isLoading}>
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          <span className="ml-1">{text.refresh}</span>
        </Button>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px]">
          <label className="text-xs text-muted-foreground">{text.search}</label>
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={text.search}
          />
        </div>
        <div className="min-w-[200px]">
          <label className="text-xs text-muted-foreground">{text.sort}</label>
          <Select value={sortKey} onValueChange={(value) => setSortKey(value as SortKey)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="recent_qc">{text.sortRecent}</SelectItem>
              <SelectItem value="oldest_qc">{text.sortOldest}</SelectItem>
              <SelectItem value="no_qc">{text.sortNoQc}</SelectItem>
              <SelectItem value="name_asc">{text.sortName}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {text.loading}
          </CardContent>
        </Card>
      ) : filteredSites.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            {text.empty}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2">{text.colBrf}</th>
                  <th className="px-4 py-2">{text.colLastQc}</th>
                  <th className="px-4 py-2">{text.colScore}</th>
                  <th className="px-4 py-2">{text.colLastCleaning}</th>
                  <th className="px-4 py-2 text-right" />
                </tr>
              </thead>
              <tbody>
                {filteredSites.map((site) => {
                  const lastQc = lastQcBySite.get(site.id) ?? null;
                  const lastCleaning = lastCleaningBySite.get(site.id) ?? null;
                  return (
                    <tr key={site.id} className="border-b last:border-b-0 hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <div className="font-medium">{site.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {site.address}
                          {site.customer_number ? ` · #${site.customer_number}` : ''}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {lastQc ? (
                          <div className="flex flex-col gap-0.5">
                            <span>{formatDate(lastQc.inspected_at)}</span>
                            {!lastQc.passed && (
                              <Badge variant="destructive" className="w-fit">
                                {language === 'sv' ? 'Underkänd' : 'Failed'}
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">{text.none}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <StarRow score={lastQc ? lastQc.score : null} />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {lastCleaning ? formatDate(lastCleaning) : <span className="text-muted-foreground">{text.none}</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => onSelectSite(site.id)}
                        >
                          {text.open}
                          <ArrowRight className="ml-1 h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
